import type {
  UseEmbeddedVideoProps,
  UseEmbeddedVideoReturn,
} from '@pearlconvert/ui/components/embedded-video/embedded-video.types'

import { useMemo } from 'react'

/**
 * Hook for handling embedded video logic
 */
export function useEmbeddedVideo({
  url,
  autoplay,
}: UseEmbeddedVideoProps): UseEmbeddedVideoReturn {
  const videoData = useMemo(() => {
    if (!url) {
      return {
        embedUrl: null,
        isYouTube: false,
        isVimeo: false,
        isValidVideo: false,
      }
    }

    const isYouTube = isYouTubeUrl(url)
    const isVimeo = isVimeoUrl(url)

    const videoId = getYouTubeVideoID(url) || getVimeoVideoID(url)

    if (!videoId) {
      return {
        embedUrl: null,
        isYouTube,
        isVimeo,
        isValidVideo: false,
      }
    }

    let embedUrl: string | null = null

    if (isYouTube) {
      embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=${
        autoplay ? 1 : 0
      }&mute=${autoplay ? 1 : 0}&rel=0&modestbranding=1`
    } else if (isVimeo) {
      embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=${
        autoplay ? 1 : 0
      }&muted=${autoplay ? 1 : 0}&title=0&byline=0&portrait=0`
    }

    return {
      embedUrl,
      isYouTube,
      isVimeo,
      isValidVideo: !!embedUrl,
    }
  }, [url, autoplay])

  return videoData
}

/**
 * Detect if a URL is YouTube
 */
const isYouTubeUrl = (url: string): boolean =>
  /(?:youtube\.com|youtu\.be)/.test(url)

/**
 * Detect if a URL is Vimeo
 */
const isVimeoUrl = (url: string): boolean => /vimeo\.com/.test(url)

/**
 * Extracts the YouTube video ID from a given URL.
 */
const getYouTubeVideoID = (url?: string): string | null => {
  if (!url) {
    return null
  }
  const regex =
    /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  const match = url.match(regex)
  return match ? match[1] : null
}

/**
 * Extracts the Vimeo video ID from a given URL.
 *
 * Examples:
 * - https://vimeo.com/123456789
 * - https://player.vimeo.com/video/123456789
 */
const getVimeoVideoID = (url?: string): string | null => {
  if (!url) {
    return null
  }
  const regex = /^(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/(?:video\/)?)(\d+)/
  const match = url.match(regex)
  return match ? match[1] : null
}
